import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { safeReturnTo } from '../utils/postLoginRedirect';

const FAILURE_REDIRECT_MS = 3000;

export default function Callback() {
  const navigate = useNavigate();
  const { handleCallback } = useAuth();
  const [error, setError] = useState(null);
  // The code-for-token exchange is ONE-SHOT: Keycloak rejects a second
  // presentation of the same code, and the state is gone from storage after the
  // first read. StrictMode mounts, unmounts and remounts every component in
  // development, so this effect runs twice on a single page load — the second
  // run fails, and its delayed "back to the root" fired three seconds after the
  // first run had already sent the reader to the shared chat they signed in for,
  // pulling them off it with nothing on screen to explain why.
  const redeemed = useRef(false);
  const failureTimer = useRef(null);

  useEffect(() => {
    if (!redeemed.current) {
      redeemed.current = true;
      processCallback();
    }
    // A redirect owed to a failure is owed to a reader who is still here. Once
    // this page is gone, so is the redirect — otherwise a pending timer
    // navigates the app from under whatever replaced it.
    return () => clearTimeout(failureTimer.current);
  }, []);

  const processCallback = async () => {
    try {
      const callbackUser = await handleCallback();
      // Back to whatever page started the sign-in — the shared chat someone was
      // asked to sign in for, or "/" when that is where they began. `replace` so
      // Back does not land on this spent callback URL.
      navigate(safeReturnTo(callbackUser?.state?.returnTo), { replace: true });
    } catch (err) {
      console.error('Callback error:', err);
      setError(err.message || 'Authentication failed');
      // Redirect to login after a short delay on error
      failureTimer.current = setTimeout(
        () => navigate('/', { replace: true }),
        FAILURE_REDIRECT_MS,
      );
    }
  };

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="bg-white dark:bg-slate-800 shadow-lg rounded-lg p-8 text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Authentication Error</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <p className="text-sm text-gray-500 dark:text-gray-500">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="bg-white dark:bg-slate-800 shadow-lg rounded-lg p-8 text-center max-w-md">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Completing sign in...</h2>
        <p className="text-gray-600 dark:text-gray-400">Please wait while we verify your credentials.</p>
      </div>
    </div>
  );
}
